/**
 * Slice-local typed message keys for the WhatsApp connect wizard.
 *
 * The central `messages.ts` is owned by another slice; this module follows the
 * same typed-key pattern locally so the wizard's copy stays type-checked and the
 * integration step can lift these keys into the shared catalogue verbatim.
 *
 * The UI chrome stays in the repository's English idiom, matching the rest of
 * the surface. The KVKK aydınlatma + açık rıza content is legal text and is kept
 * in Turkish exactly as the approved notice reads; bracketed [YER TUTUCU]
 * markers are preserved so the notice is never presented as more settled than it
 * is — under `v1-ictest-2026-08-08` the data-controller fields are genuinely
 * still unfilled, and the label below must not claim otherwise.
 */

/**
 * The version label the consent step displays. It is a *copy* of the server's
 * WHATSAPP_CONSENT_TEXT_VERSION (apps/api/src/whatsapp/contracts.ts), which is
 * the value actually stamped on the connection and audited — the browser never
 * sends a version. The two must stay identical: if this one drifts, the member
 * is shown a version string different from the one they are in fact consenting
 * under, which is the one thing a consent record has to get right.
 */
export const WHATSAPP_CONSENT_TEXT_VERSION = "v1-ictest-2026-08-08" as const;

export const whatsappMessages = {
  dialog: {
    title: "WhatsApp bağla",
    description:
      "Rızayı gözden geçirin, sonra kontrolünüzdeki WhatsApp Business hesabını bağlayın.",
  },
  tag: "WhatsApp bağla",
  breadcrumbSync: "Eşitleme",
  breadcrumbAdd: "Kaynak ekle",
  stepper: {
    label: "Kurulum ilerlemesi",
    stepPrefix: (index: number) => `${index}. adım`,
    consent: "Rıza",
    linkDevice: "Cihaz bağla",
    connected: "Bağlandı",
  },
  consent: {
    eyebrow: "Cihaz bağlamadan önce",
    title: "Obsina, WhatsApp Business iletilerinizle ne yapacak",
    lede: "Cihaz bağlamak, şirket hafızanızı oluşturabilmesi için Obsina'nın bu WhatsApp Business hesabını okumasına izin verir. Devam etmeden önce buna nelerin dâhil olduğunu okuyun.",
    points: [
      {
        title: "Gelen ve giden iletiler",
        body: "Obsina, bu kurumsal hesapta gönderilen ve alınan iletileri şirket hafızanıza alır — kişisel sohbetlerinizi değil.",
      },
      {
        title: "Erişim yetkinizin sınırında kalır",
        body: "Bu kaynaktan oluşanı yalnızca çalışma alanınızda yetkilendirdiğiniz kişiler görebilir. Görünürlük kaynak başına belirlenir ve yeniden rıza olmadan genişletilemez.",
      },
      {
        title: "Sunucuda, şifreli saklanır",
        body: "Bağlı cihaz oturumu Obsina'nın sunucularında durur. Dilediğiniz an bağlantıyı kesebilirsiniz; bu, yeni iletilerin eşitlenmesini durdurur.",
      },
    ],
    // KVKK aydınlatma (A) — Turkish legal text, shown as expandable details.
    noticeToggle: "Aydınlatma Metni (ayrıntılar)",
    noticeTitle:
      "Kişisel Verilerin İşlenmesine İlişkin Aydınlatma Metni (6698 s. Kanun m.10)",
    noticeSections: [
      {
        heading: "1. Veri sorumlusu",
        body: "Bu aydınlatma, veri sorumlusu sıfatıyla [ISOTEC Enerji A.Ş. — tam yasal unvan teyit edilecek] (“Şirket”) tarafından yapılmaktadır. Adres, KEP/başvuru e-postası ve MERSİS/VKN bilgileri [YER TUTUCU] olarak kesinleştirilecektir.",
      },
      {
        heading: "2. İşlenen kişisel veriler",
        body: "Bağladığınız kurumsal iletişim amaçlı WhatsApp hesabı üzerinden geçen mesaj metinleri; gönderilen/alınan medya ve eklerin meta verileri; gönderen/alıcı numarası ve görünen ad; grup adı ve katılımcı listesi; mesaj kimliği, zaman damgası, yön ve sohbet türü işlenir.",
      },
      {
        heading: "3. İşleme amaçları",
        body: "Kurumsal iletişimin erişim kontrollü şirket hafızası olarak envantere alınması ve korunması; iş sürekliliği; yetki dahilinde soru-cevap ve kanıta bağlı bilgi erişimi; iş taahhütlerinin takibi; hukuki yükümlülükler ve ispat. Amaç “her şeyin izlenmesi” değildir.",
      },
      {
        heading: "4. Hukuki sebep",
        body: "İşlemenin birincil dayanağı Şirketin meşru menfaatidir (m.5/2-f); kurumsal iletişimle sınırlı ve ölçülüdür. WhatsApp hesabınızı bağlama eyleminiz ek bir açık rıza katmanı (m.5/1) olarak alınır. Bu rızayı vermek zorunda değilsiniz; vermemeniz iş ilişkinizi etkilemez ve rızanızı dilediğiniz zaman geri çekebilirsiniz.",
      },
      {
        heading: "5. Kapsam dürüstlüğü — gelen, giden ve grup mesajları",
        body: "Bağladığınız hesap üzerinden gelen, giden ve grup mesajları işlenir. Grup sohbetlerinde diğer üyelerin mesajları da akışa dahildir; bu kişiler ayrı birer ilgili kişidir ve onlara yönelik aydınlatma ayrı bir mekanizmayla sağlanır.",
      },
      {
        heading: "6. Ölçülülük ve minimizasyon",
        body: "Salt-okuma / pasif ingest: sistem sizin adınıza mesaj göndermez. Her kayıt görünürlük etiketiyle tutulur; owner_private yalnız sahibine, tenant_shared yalnız yetkili üyelere görünür. “Yönetici her şeyi görür” modeli uygulanmaz. Özel nitelikli verilerin ayıklanması esastır [YER TUTUCU].",
      },
      {
        heading: "7. Aktarım",
        body: "Veriler yalnız yetki ve görünürlük kuralları çerçevesinde aynı kurumun yetkili üyelerine sunulur. Sorgulama/analiz özelliklerinde kullanılan yapay zekâ ve altyapı hizmet sağlayıcıları ve konumları [YER TUTUCU]; yurt dışı aktarım söz konusuysa m.9 mekanizması uygulanır.",
      },
      {
        heading: "8. Toplama yöntemi ve saklama",
        body: "Veriler QR/eşleştirme kodu ile kendi hesabınızı bağlamanız yoluyla elektronik ve otomatik yollarla toplanır. Saklama süresi işleme amacının gerektirdiği ve mevzuattaki zamanaşımı süreleri boyuncadır [somut süre — YER TUTUCU].",
      },
      {
        heading: "9. Haklarınız (m.11)",
        body: "İşlenip işlenmediğini öğrenme, bilgi ve amaç talebi, aktarılan üçüncü kişileri bilme, düzeltme, silme/yok etme, bunların üçüncü kişilere bildirilmesi, otomatik analize itiraz ve zararın giderilmesini talep haklarına sahipsiniz. Başvuru kanalı: [KEP / kvkk@… — YER TUTUCU].",
      },
    ],
    kvkkCallout:
      "KVKK / GDPR uyarısı. WhatsApp Business içeriği çalışan ve üçüncü kişilerin kişisel verilerini içerebilir. İşlemek için hukuki dayanağınız olduğundan emin olun. Kontrolünüzdeki bir kurumsal hesabı bağlayın.",
    // KVKK açık rıza (B) — checkbox label + supporting sentence.
    checkboxLabel: "Okudum, anladım ve açık rıza veriyorum.",
    checkboxDetail:
      "Kurumsal iletişim amacıyla kullandığım WhatsApp hesabımı Obsina'ya bağlamayı; bu hesap üzerinden geçen gelen, giden ve grup mesajlarımın Aydınlatma Metni'nde belirtilen amaçlarla, salt-okuma ve erişim kontrollü biçimde işlenmesini özgür irademle kabul ediyorum. Rızamı dilediğim zaman geri çekebileceğimi biliyorum.",
    versionLabel: (version: string) => `Metin sürümü: ${version}`,
    footHint: "Kimin görebileceğini sonraki adımda seçersiniz",
    cancel: "Vazgeç",
    continue: "Devam",
    submitting: "Rıza kaydediliyor…",
    error: "Rıza kaydedilemedi. Yeniden deneyin.",
  },
  pairing: {
    eyebrow: "Bu cihazı bağla",
    title: "QR kodunu WhatsApp ile tarayın",
    lede: "WhatsApp Business hesabınızın çalıştığı telefonda WhatsApp'ı açın ve Obsina'yı cihaz olarak bağlamak için bu kodu tarayın.",
    instructionsTitle: "Nasıl taranır",
    instructions: [
      "Telefonunuzda WhatsApp'ı açın.",
      "Ayarlar'a, sonra Bağlı cihazlar'a dokunun.",
      "Cihaz bağla'ya dokunup kamerayı koda tutun.",
    ],
    qrAlt: "WhatsApp bağlama QR kodu",
    countdown: (clock: string) => `Kod ${clock} sonra yenilenir`,
    expired:
      "Bu kodun süresi doldu. Bağlanmayı sürdürmek için yeni bir kod alın.",
    waiting:
      "Bu sekmeyi açık tutun. Kod tarandığında Obsina kendiliğinden bağlanır.",
    waitingStatus: "Kodun taranması bekleniyor.",
    preparing: "Bağlama kodunuz hazırlanıyor…",
    scanning: "Kod tarandı — cihazınız bağlanıyor…",
    scanningStatus: "QR kodu tarandı. WhatsApp cihazınız bağlanıyor.",
    newCode: "Yeni kod",
    refreshing: "Yenileniyor…",
    back: "Geri",
    loggedOut:
      "Bağlı cihazın oturumu kapatıldı. Yeniden bağlamak için yeni bir kod alın.",
    error: "Bağlama kodu yüklenemedi. Yeniden deneyin.",
    retry: "Yeniden dene",
    // Phone-number code flow (for phones that cannot scan their own screen).
    code: {
      toggle: "Tarayamıyor musunuz? Telefon numaranızla bağlayın",
      qrToggle: "Bunun yerine QR kodunu kullan",
      eyebrow: "Telefon numaranızla bağlayın",
      title: "WhatsApp'ı kodla bağlayın",
      lede: "WhatsApp Business hesabının telefon numarasını girin, sonra aşağıdaki kodu o telefondaki WhatsApp'a yazın.",
      countryLabel: "Ülke kodu",
      otherCountry: "Diğer…",
      customCodeLabel: "Alan kodu",
      customCodePlaceholder: "örn. 90",
      phoneLabel: "Telefon numarası",
      phoneHint:
        "Nasıl isterseniz yazın — boşluklu, tireli ya da başında 0 olsun olmasın. Obsina düzeltir.",
      phonePlaceholder: "532 123 45 67",
      phoneInvalid: "Bu numara eksik görünüyor. Rakamları kontrol edin.",
      preview: (number: string) => `Bağlanacak numara: ${number}`,
      getCode: "Bağlama kodu al",
      gettingCode: "Kod isteniyor…",
      codeTitle: "Bu kodu WhatsApp'a girin",
      codeHeading: "Bağlama kodunuz",
      instructionsTitle: "Kod nasıl girilir",
      instructions: [
        "Bu numaranın olduğu telefonda WhatsApp'ı açın.",
        "Ayarlar'a, sonra Bağlı cihazlar'a, sonra Cihaz bağla'ya dokunun.",
        "Phone numberyla bağla'ya dokunup kodu girin.",
      ],
      copy: "Kodu kopyala",
      copied: "Kod kopyalandı",
      waiting:
        "Bu sekmeyi açık tutun. Kodu girdiğinizde Obsina kendiliğinden bağlanır.",
      waitingStatus: (code: string) =>
        `WhatsApp bağlama kodunuz ${code.split("").join(" ")}. Telefonunuzdaki WhatsApp'a girin.`,
      newCode: "Yeni kod",
      changeNumber: "Başka numara kullan",
    },
  },
  connected: {
    title: "WhatsApp bağlandı",
    subtitle: "Cihaz olarak bağlandı",
    cardName: "WhatsApp Business",
    cardStatus: "Hafıza kuruluyor · ilk eşitleme başladı",
    statusLabel: "Eşitleniyor",
    scopeNote: "Bu kaynak size özel olarak başlar.",
    connectedStatus: "WhatsApp bağlandı. İlk eşitleme başladı.",
    privateStatus: "Kaynak yalnızca size özel.",
    goToSyncControl: "Eşitleme'ye git",
    reviewScope: "Kapsamı gözden geçir",
  },
} as const;

export type WhatsAppMessages = typeof whatsappMessages;
